import { useState, useEffect } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { Music2, Search, Plus, LinkIcon, Loader2, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * SpotifyImportDialog — 2 tabs:
 *   1) Search — type track/artist name, browse results, click "Add" per row
 *   2) Import URL — paste a Spotify URL (track / artist / playlist) → bulk add
 *
 * All adds POST /api/playlists/tracks/spotify. Fires `onAdded()` after each
 * successful add so the parent list refreshes.
 */
export default function SpotifyImportDialog({ onAdded, trigger }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("search"); // "search" | "url"
  const [enabled, setEnabled] = useState(true);

  // Search state
  const [q, setQ] = useState("");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [addedIds, setAddedIds] = useState(new Set());

  // URL import state
  const [url, setUrl] = useState("");
  const [importing, setImporting] = useState(false);
  const [importReport, setImportReport] = useState(null);

  useEffect(() => {
    if (!open) return;
    // Check whether Spotify is configured on this instance
    api.get("/spotify/config")
      .then(({ data }) => setEnabled(!!data?.configured))
      .catch(() => setEnabled(false));
  }, [open]);

  const runSearch = async (e) => {
    e?.preventDefault?.();
    if (!q.trim()) return;
    setSearching(true);
    try {
      const { data } = await api.get(`/spotify/search`, { params: { q: q.trim(), limit: 20 } });
      setResults(data.items || data.tracks || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSearching(false); }
  };

  const addTrack = async (item) => {
    try {
      await api.post("/playlists/tracks/spotify", {
        spotify_id: item.id || item.spotify_id,
        title: item.name || item.title,
        artist: item.artist || item.artist_name || (item.artists || []).map((a) => a.name || a).join(", "),
        preview_url: item.preview_url || null,
        spotify_url: item.spotify_url || item.external_urls?.spotify || null,
        artwork_url: item.artwork_url || item.album?.images?.[0]?.url || null,
        duration_ms: item.duration_ms || null,
      });
      setAddedIds((s) => new Set([...s, item.id || item.spotify_id]));
      if (onAdded) onAdded();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const importUrl = async () => {
    if (!url.trim()) return toast.error("Paste a Spotify URL.");
    setImporting(true);
    setImportReport(null);
    try {
      // Resolve the URL to a { kind, id } (track | artist | playlist).
      const { data: r } = await api.post("/spotify/resolve", { url: url.trim() });
      const kind = r.kind;
      let tracks = [];
      if (kind === "track") {
        tracks = [r.track];
      } else if (kind === "playlist") {
        const { data: pl } = await api.get(`/spotify/playlist/${r.id}`);
        tracks = pl.tracks || pl.items || [];
      } else if (kind === "artist") {
        const { data: ar } = await api.get(`/spotify/artist/${r.id}`);
        tracks = ar.top_tracks || ar.tracks || [];
      } else {
        toast.error("Unsupported Spotify URL type.");
        setImporting(false);
        return;
      }
      let ok = 0, fail = 0;
      for (const t of tracks) {
        try {
          await api.post("/playlists/tracks/spotify", {
            spotify_id: t.id || t.spotify_id,
            title: t.name || t.title,
            artist: t.artist || t.artist_name || (t.artists || []).map((a) => a.name || a).join(", "),
            preview_url: t.preview_url || null,
            spotify_url: t.spotify_url || t.external_urls?.spotify || null,
            artwork_url: t.artwork_url || t.album?.images?.[0]?.url || null,
            duration_ms: t.duration_ms || null,
          });
          ok++;
        } catch { fail++; }
      }
      setImportReport({ kind, ok, fail });
      if (onAdded) onAdded();
      if (ok > 0) toast.success(`Imported ${ok} track${ok !== 1 ? "s" : ""} from Spotify.`);
      if (fail > 0) toast.error(`${fail} track${fail !== 1 ? "s" : ""} failed to import.`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't resolve that Spotify URL.");
    } finally { setImporting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button
            variant="outline"
            className="rounded-lg border-[#1DB954] text-[#1DB954] hover:bg-[#1DB954] hover:text-white h-11 px-4 font-semibold"
            data-testid="spotify-import-button"
          >
            <Music2 size={16} className="mr-1.5" /> Import from Spotify
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-lg" data-testid="spotify-import-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Music2 size={18} className="text-[#1DB954]" /> Add from Spotify
          </DialogTitle>
          <DialogDescription>
            Search Spotify&apos;s catalog or paste a track / artist / playlist URL to import.
          </DialogDescription>
        </DialogHeader>

        {!enabled ? (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            Spotify isn&apos;t configured on this instance yet. Ask an admin to set{" "}
            <code className="font-mono text-[11px]">SPOTIFY_CLIENT_ID</code> +{" "}
            <code className="font-mono text-[11px]">SPOTIFY_CLIENT_SECRET</code>.
          </div>
        ) : (
          <>
            {/* Tab switcher */}
            <div className="inline-flex rounded-lg border border-[#EAE3DE] bg-[#FAF5F2] p-1">
              {[
                { v: "search", label: "Search", Icon: Search },
                { v: "url", label: "Import URL", Icon: LinkIcon },
              ].map((t) => (
                <button
                  key={t.v}
                  type="button"
                  onClick={() => setTab(t.v)}
                  className={`px-3 py-1.5 text-[12px] font-semibold rounded-md transition-colors inline-flex items-center gap-1.5 ${
                    tab === t.v ? "bg-white text-[#1B1614] shadow-sm" : "text-[#8B7B73] hover:text-[#1B1614]"
                  }`}
                  data-testid={`spotify-tab-${t.v}`}
                >
                  <t.Icon size={12} /> {t.label}
                </button>
              ))}
            </div>

            {tab === "search" && (
              <div className="space-y-3">
                <form onSubmit={runSearch} className="flex gap-2">
                  <Input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Track name, artist, album…"
                    className="rounded-md"
                    data-testid="spotify-search-input"
                  />
                  <Button
                    type="submit"
                    disabled={searching}
                    className="rounded-md bg-[#1DB954] hover:bg-[#189745] text-white shrink-0"
                    data-testid="spotify-search-btn"
                  >
                    {searching ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
                  </Button>
                </form>
                {results.length === 0 && !searching && (
                  <p className="text-[12px] text-[#8B7B73] text-center py-4">
                    Type to search Spotify&apos;s catalog.
                  </p>
                )}
                <ul className="max-h-72 overflow-auto divide-y divide-[#EAE3DE]" data-testid="spotify-search-results">
                  {results.map((r) => {
                    const sid = r.id || r.spotify_id;
                    const added = addedIds.has(sid);
                    const artistLabel = r.artist || r.artist_name || (r.artists || []).map((a) => a.name || a).join(", ");
                    const art = r.artwork_url || r.album?.images?.[0]?.url;
                    return (
                      <li key={sid} className="flex items-center gap-3 py-2" data-testid={`spotify-result-${sid}`}>
                        <div className="w-10 h-10 rounded bg-[#FAF5F2] overflow-hidden shrink-0">
                          {art ? <img src={art} alt="" className="w-full h-full object-cover" /> : <Music2 size={14} className="m-auto text-[#8B7B73] mt-3" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-[13px] font-semibold text-[#1B1614] truncate">{r.name || r.title}</div>
                          <div className="text-[11px] text-[#8B7B73] truncate">{artistLabel}</div>
                        </div>
                        <Button
                          size="sm"
                          variant={added ? "outline" : "default"}
                          onClick={() => addTrack(r)}
                          disabled={added}
                          className={added
                            ? "rounded-md text-emerald-700 border-emerald-300"
                            : "rounded-md bg-[#B31E1E] hover:bg-[#8F1717] text-white"}
                          data-testid={`spotify-add-${sid}`}
                        >
                          {added ? <><Check size={12} className="mr-1" /> Added</> : <><Plus size={12} className="mr-1" /> Add</>}
                        </Button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            {tab === "url" && (
              <div className="space-y-3">
                <Input
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://open.spotify.com/playlist/… or /track/… or /artist/…"
                  className="rounded-md font-mono text-[12px]"
                  data-testid="spotify-url-input"
                />
                <p className="text-[11px] text-[#8B7B73] leading-snug">
                  Track URLs add one song. Playlist URLs import every track. Artist URLs import that artist&apos;s top 10 tracks.
                </p>
                <Button
                  onClick={importUrl}
                  disabled={importing}
                  className="w-full rounded-md bg-[#1DB954] hover:bg-[#189745] text-white font-semibold"
                  data-testid="spotify-import-url-btn"
                >
                  {importing ? <><Loader2 size={14} className="mr-1.5 animate-spin" /> Importing…</> : <>Import</>}
                </Button>
                {importReport && (
                  <div className="rounded-md border border-[#EAE3DE] bg-[#FAF5F2] px-3 py-2 text-[12px]" data-testid="spotify-import-report">
                    <div className="font-semibold text-[#1B1614]">Import complete</div>
                    <div className="text-[#5C4E48] mt-0.5">
                      {importReport.kind}: <span className="font-mono text-emerald-700">{importReport.ok} added</span>
                      {importReport.fail > 0 && <> · <span className="font-mono text-red-700">{importReport.fail} failed</span></>}
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
